import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useEffect,
  type ReactNode,
  type Dispatch,
} from "react";
import type {
  Phase,
  Role,
  Topic,
  RequirementFields,
  Evaluation,
  Output,
  PhaseConfig,
} from "@offsite/shared";

export interface AuthState {
  token: string | null;
  role: Role | null;
  /** Reviewer-Magic-Link sessions: read-only — every mutation button must
   *  consult `useReadOnly()` and disable itself. */
  readOnly: boolean;
}

export interface WinningTopicState {
  topic: Topic;
  count: number;
}

export interface WorkshopState {
  phase: PhaseConfig;
  topics: Topic[];
  groupStats: { topicId: string; count: number; writerName?: string }[];
  myTopicId: string | null;
  myGroupRole: "writer" | "viewer" | null;
  myName: string | null;
  requirement: RequirementFields | null;
  evaluations: Record<number, Evaluation>;
  outputs: Record<string, Output>;
  /**
   * Winning topic, polled centrally by useWinningTopicPolling once per
   * app instance — consumers read it via useWinningTopic (no own fetch).
   */
  winningTopic: WinningTopicState | null;
}

export interface AppState {
  auth: AuthState;
  workshop: WorkshopState;
}

export type AppAction =
  | { type: "auth/login"; token: string; role: Role; readOnly?: boolean }
  | { type: "auth/logout" }
  | { type: "phase/set"; phase: PhaseConfig }
  | { type: "topics/set"; topics: Topic[] }
  | { type: "groups/stats"; stats: WorkshopState["groupStats"] }
  | { type: "group/join"; topicId: string; role: "writer" | "viewer"; name: string }
  | { type: "requirement/set"; fields: RequirementFields }
  | { type: "evaluations/set"; evaluations: Record<number, Evaluation> }
  | { type: "outputs/set"; outputs: Record<string, Output> }
  | { type: "winningTopic/set"; value: WinningTopicState | null };

const STORAGE_KEY = "offsite:auth";

const initialState: AppState = {
  auth: loadAuth(),
  workshop: {
    phase: { phase: "waiting", updatedAt: 0 },
    topics: [],
    groupStats: [],
    myTopicId: null,
    myGroupRole: null,
    myName: null,
    requirement: null,
    evaluations: {},
    outputs: {},
    winningTopic: null,
  },
};

function loadAuth(): AuthState {
  if (typeof window === "undefined") {
    return { token: null, role: null, readOnly: false };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { token: null, role: null, readOnly: false };
    const parsed = JSON.parse(raw) as Partial<AuthState>;
    return {
      token: parsed.token ?? null,
      role: parsed.role ?? null,
      readOnly: parsed.readOnly === true,
    };
  } catch {
    return { token: null, role: null, readOnly: false };
  }
}

function persistAuth(auth: AuthState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
  } catch {
    /* noop */
  }
}

function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "auth/login":
      return {
        ...state,
        auth: {
          token: action.token,
          role: action.role,
          readOnly: action.readOnly === true,
        },
      };
    case "auth/logout":
      return { ...state, auth: { token: null, role: null, readOnly: false } };
    case "phase/set":
      // Ein unbrauchbarer Wert darf den Store nicht zerlegen. Vorher genügte ein
      // Endpunkt, der einmal etwas anderes als eine PhaseConfig liefert, und
      // `usePhase()` warf beim nächsten Render — mitten in der Sitzung, auf der
      // Leinwand. Lieber den alten Stand behalten: der Poll holt den richtigen
      // in drei Sekunden nach.
      if (!action.phase || typeof action.phase.phase !== "string") {
         
        console.error("phase/set ohne brauchbare Phase — ignoriert", action.phase);
        return state;
      }
      return { ...state, workshop: { ...state.workshop, phase: action.phase } };
    case "topics/set":
      return { ...state, workshop: { ...state.workshop, topics: action.topics } };
    case "groups/stats":
      return { ...state, workshop: { ...state.workshop, groupStats: action.stats } };
    case "group/join":
      return {
        ...state,
        workshop: {
          ...state.workshop,
          myTopicId: action.topicId,
          myGroupRole: action.role,
          myName: action.name,
        },
      };
    case "requirement/set":
      return { ...state, workshop: { ...state.workshop, requirement: action.fields } };
    case "evaluations/set":
      return { ...state, workshop: { ...state.workshop, evaluations: action.evaluations } };
    case "outputs/set":
      return { ...state, workshop: { ...state.workshop, outputs: action.outputs } };
    case "winningTopic/set":
      return {
        ...state,
        workshop: { ...state.workshop, winningTopic: action.value },
      };
    default:
      return state;
  }
}

type StoreValue = { state: AppState; dispatch: Dispatch<AppAction> };

const Store = createContext<StoreValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    persistAuth(state.auth);
  }, [state.auth]);

  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <Store.Provider value={value}>{children}</Store.Provider>;
}

export function useStore() {
  const ctx = useContext(Store);
  if (!ctx) throw new Error("useStore must be used inside <StoreProvider>");
  return ctx;
}

export function useAuth() {
  return useStore().state.auth;
}

export function useWorkshop() {
  return useStore().state.workshop;
}

export function usePhase(): Phase {
  return useStore().state.workshop.phase.phase;
}

/**
 * Hook for mutation buttons: returns `true` when the active session is a
 * Reviewer-Magic-Link (read-only). Wire this into every button that calls
 * a mutating endpoint:
 *   const readOnly = useReadOnly();
 *   <button disabled={busy || readOnly} title={readOnly ? READ_ONLY_TITLE : undefined}>
 */
export function useReadOnly(): boolean {
  return useStore().state.auth.readOnly === true;
}

/** Tooltip text used everywhere a button gets greyed out for reviewers. */
export const READ_ONLY_TITLE =
  "Nur Lesezugriff — Mutationen sind in dieser Session deaktiviert";
